import { DataGrid, GridColDef, GridRenderCellParams } from '@mui/x-data-grid';
import { Alert, Box, Chip, IconButton, Link, Tooltip, Typography } from '@mui/material';
import {
  Check as CheckIcon,
  Close as CloseIcon,
  OpenInNew as OpenInNewIcon,
  Assessment as AssessmentIcon,
  Language as LanguageIcon,
} from '@mui/icons-material';

interface CompanyInfo {
  dbId?: number | null;
  uniqueDomain?: string | null;
  shortName?: string | null;
  websiteUrl?: string | null;
}

interface PendingMatch {
  dbId: number;
  companyId: number;
  matchedIn: string | null;
  matchedText: string | null;
  matchConfidence: number | null;
  wasCorrect: boolean | null;
  userFeedback: string | null;
  datetimeCreated: string | null;
  pattern: {
    patternText: string;
    classificationTarget: string;
  } | null;
  companyInfo?: CompanyInfo | null;
}

interface PendingMatchesDataGridProps {
  matches: PendingMatch[];
  onMarkCorrect: (matchId: number) => Promise<void>;
  onMarkIncorrect: (matchId: number) => Promise<void>;
  loading?: boolean;
}

const TARGET_COLORS: Record<string, 'success' | 'warning' | 'error' | 'info' | 'default'> = {
  software: 'success',
  services: 'warning',
  reseller: 'info',
  agency: 'default',
  non_software: 'error',
};

const PendingMatchesDataGrid = ({
  matches,
  onMarkCorrect,
  onMarkIncorrect,
  loading = false,
}: PendingMatchesDataGridProps) => {
  const columns: GridColDef[] = [
    {
      field: 'pattern',
      headerName: 'Pattern',
      flex: 1,
      minWidth: 150,
      renderCell: (params: GridRenderCellParams<PendingMatch>) => (
        <Box>
          <Typography variant="body2" fontWeight={600}>
            {params.row.pattern?.patternText || 'Unknown'}
          </Typography>
          <Chip
            label={params.row.pattern?.classificationTarget || 'unknown'}
            size="small"
            color={TARGET_COLORS[params.row.pattern?.classificationTarget || ''] || 'default'}
            sx={{ mt: 0.5 }}
          />
        </Box>
      ),
    },
    {
      field: 'companyInfo',
      headerName: 'Company',
      flex: 1.5,
      minWidth: 200,
      renderCell: (params: GridRenderCellParams<PendingMatch>) => {
        const company = params.row.companyInfo;
        const domain = company?.uniqueDomain || `ID: ${params.row.companyId}`;
        const companyId = company?.dbId || params.row.companyId;

        return (
          <Box>
            <Typography variant="body2" fontWeight={600} sx={{ mb: 0.5 }}>
              {domain}
            </Typography>
            <Box display="flex" gap={0.5}>
              <Tooltip title="Open in DFM assessment">
                <IconButton
                  size="small"
                  component={Link}
                  href={`/evaluation/dfm?companyId=${companyId}`}
                  target="_blank"
                  sx={{ p: 0.5 }}
                >
                  <AssessmentIcon fontSize="small" color="primary" />
                </IconButton>
              </Tooltip>
              {company?.websiteUrl && (
                <Tooltip title={`Visit ${company.websiteUrl}`}>
                  <IconButton
                    size="small"
                    component={Link}
                    href={company.websiteUrl.startsWith('http') ? company.websiteUrl : `https://${company.websiteUrl}`}
                    target="_blank"
                    rel="noopener noreferrer"
                    sx={{ p: 0.5 }}
                  >
                    <LanguageIcon fontSize="small" color="action" />
                  </IconButton>
                </Tooltip>
              )}
              {!company?.websiteUrl && company?.uniqueDomain && (
                <Tooltip title={`Visit https://${company.uniqueDomain}`}>
                  <IconButton
                    size="small"
                    component={Link}
                    href={`https://${company.uniqueDomain}`}
                    target="_blank"
                    rel="noopener noreferrer"
                    sx={{ p: 0.5 }}
                  >
                    <LanguageIcon fontSize="small" color="action" />
                  </IconButton>
                </Tooltip>
              )}
            </Box>
          </Box>
        );
      },
    },
    {
      field: 'matchedIn',
      headerName: 'Source',
      width: 120,
      renderCell: (params: GridRenderCellParams<PendingMatch>) => (
        <Chip label={params.value || 'unknown'} size="small" variant="outlined" />
      ),
    },
    {
      field: 'matchedText',
      headerName: 'Matched Text',
      flex: 2,
      minWidth: 200,
      renderCell: (params: GridRenderCellParams<PendingMatch>) => (
        <Tooltip title={params.value || ''}>
          <Typography
            variant="body2"
            sx={{
              overflow: 'hidden',
              textOverflow: 'ellipsis',
              whiteSpace: 'nowrap',
              maxWidth: '100%',
            }}
          >
            {params.value || '-'}
          </Typography>
        </Tooltip>
      ),
    },
    {
      field: 'datetimeCreated',
      headerName: 'Date',
      width: 100,
      renderCell: (params: GridRenderCellParams<PendingMatch>) => {
        if (!params.value) return '-';
        const date = new Date(params.value);
        return (
          <Typography variant="caption" color="text.secondary">
            {date.toLocaleDateString()}
          </Typography>
        );
      },
    },
    {
      field: 'actions',
      headerName: 'Feedback',
      width: 140,
      sortable: false,
      renderCell: (params: GridRenderCellParams<PendingMatch>) => (
        <Box display="flex" gap={0.5}>
          <Tooltip title="Correct match: Company is NOT software (confirm pattern)">
            <IconButton
              size="small"
              onClick={() => onMarkCorrect(params.row.dbId)}
              color="success"
              sx={{
                border: '1px solid',
                borderColor: 'success.light',
                '&:hover': { bgcolor: 'success.light', color: 'white' },
              }}
            >
              <CheckIcon fontSize="small" />
            </IconButton>
          </Tooltip>
          <Tooltip title="Wrong match: Company IS software (pattern was wrong)">
            <IconButton
              size="small"
              onClick={() => onMarkIncorrect(params.row.dbId)}
              color="error"
              sx={{
                border: '1px solid',
                borderColor: 'error.light',
                '&:hover': { bgcolor: 'error.light', color: 'white' },
              }}
            >
              <CloseIcon fontSize="small" />
            </IconButton>
          </Tooltip>
        </Box>
      ),
    },
  ];

  return (
    <Box>
      <Alert severity="info" sx={{ mb: 2 }}>
        <Typography variant="body2">
          <strong>Pattern Feedback:</strong> These patterns detected signals that suggest the company is{' '}
          <strong>NOT software</strong>. Click{' '}
          <CheckIcon sx={{ fontSize: 14, verticalAlign: 'middle', color: 'success.main' }} /> if the company is indeed{' '}
          <strong>not software</strong> (pattern was correct). Click{' '}
          <CloseIcon sx={{ fontSize: 14, verticalAlign: 'middle', color: 'error.main' }} /> if the company{' '}
          <strong>is software</strong> (pattern was misleading).
        </Typography>
      </Alert>
      <DataGrid
        rows={matches}
        columns={columns}
        getRowId={row => row.dbId}
        loading={loading}
        pageSizeOptions={[25, 50, 100]}
        initialState={{
          pagination: { paginationModel: { pageSize: 25 } },
          sorting: { sortModel: [{ field: 'datetimeCreated', sort: 'desc' }] },
        }}
        sx={{
          height: '100%',
          '& .MuiDataGrid-cell': {
            borderBottom: '1px solid rgba(224, 224, 224, 0.5)',
          },
          '& .MuiDataGrid-row:hover': {
            backgroundColor: 'rgba(0, 0, 0, 0.02)',
          },
        }}
        disableRowSelectionOnClick
        rowHeight={70}
      />
    </Box>
  );
};

export default PendingMatchesDataGrid;
